import { ThemeProvider } from '@mui/material/styles';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { ApiErrorResponse, EmployerProfile, UserProfile } from './common/types';
import AccessibeFAB from './components/Accessible';
import FeedbackButton from './components/FeedbackButton';
import FeedbackDialog from './components/FeedbackDialog';
import { DialogProvider } from './components/dialogs';
import Layout from './components/Layout';
import SiteMessageBanner from './components/SiteMessageBanner';
import { LoadingOverlay } from './components/shared/ui/Loading';
import { NotificationManager } from './components/ToastManager';
import {
  useEmployerProfile,
  useTouStatus,
  useUserProfile,
} from './hooks/queries/useUserQueries';
import { useErrorHandler } from './hooks/utils/useErrorHandler';
import { useSessionGate } from './hooks/utils/useSessionGate';
import PageNotFound from './pages/404';
import GeneralErrorPage from './pages/GeneralErrorPage';
import Landing from './pages/Landing';
import Login from './pages/Login';
import LoginCallback from './pages/LoginCallback';
import RequestAccess from './pages/RequestAccess';
import QueryProvider from './providers/QueryProvider';
import useStore from './stores';
import McGovernTheme from './theme/mcGovern';
import currentSession from './utils/currentSession';

// Lazy pages
const SetupPage = lazy(() => import('./pages/Setup'));
const TouPage = lazy(() => import('./pages/Tou'));
const HomePage = lazy(() => import('./pages/Home'));
const OrganizationPage = lazy(() => import('./pages/Organization'));
const GranteesPage = lazy(() => import('./pages/Grantees'));
const AnalysisPage = lazy(() => import('./pages/analysis'));
const AnalysisCreatePage = lazy(
  () => import('./pages/analysis/AnalysisCreate'),
);
const AnalysisReportPage = lazy(
  () => import('./pages/analysis/report/[reportId]'),
);
const GranteePage = lazy(() => import('./pages/grantee/[granteeId]'));
const GranteeFormPage = lazy(() => import('./pages/grantee/[granteeId]/add'));
const GranteeEditPage = lazy(() => import('./pages/grantee/[granteeId]/edit'));
const DocumentPage = lazy(
  () => import('./pages/grantee/[granteeId]/document/[documentId]'),
);
const RiskProfilePage = lazy(() => import('./pages/risk-profile'));
const UserManagementPage = lazy(() => import('./pages/UserManagement'));
const UserProfilePage = lazy(() => import('./pages/UserProfile'));

const HelpCenterPage = lazy(() => import('./pages/help'));

function RouterChildren() {
  const [loading, setLoadStatus] = useState(false);
  const [failedRequest, setFailedRequest] = useState<ApiErrorResponse | null>(
    null,
  );

  // Use new error handling system
  const { handleError } = useErrorHandler({
    context: 'application navigation',
  });

  const userProfile = useStore((state) => state.userProfile);
  const setUserProfile = useStore((state) => state.setUserProfile);
  const employerProfile = useStore((state) => state.employerProfile);
  const setEmployerProfile = useStore((state) => state.setEmployerProfile);
  const touAccepted = useStore((state) => state.touAccepted);
  const setTouAccepted = useStore((state) => state.setTouAccepted);
  const [, setUserEmail] = useState(userProfile?.email);
  const navigate = useNavigate();
  const location = useLocation();

  // Use React Query hooks for data fetching
  const { refetch: refetchUserProfile } = useUserProfile();
  const { refetch: refetchEmployerProfile } = useEmployerProfile(
    userProfile?.employer,
  );
  const { refetch: refetchTouStatus } = useTouStatus();

  const asyncSetUserProfile = useCallback(
    (profile: UserProfile) =>
      Promise.resolve().then(() => setUserProfile(profile)),
    [setUserProfile],
  );

  const asyncSetEmployerProfile = useCallback(
    (profile: EmployerProfile) =>
      Promise.resolve().then(() => setEmployerProfile(profile)),
    [setEmployerProfile],
  );

  function emptyOrUndefined(
    obj: UserProfile | EmployerProfile | null | undefined,
  ) {
    return (
      obj === undefined ||
      obj === null ||
      (Object.keys(obj).length === 0 && obj.constructor === Object)
    );
  }

  const handleNavigation = useCallback(() => {
    const currentPath = location.pathname;

    if (!touAccepted && currentPath !== '/tou') {
      navigate('/tou');
    } else if (
      emptyOrUndefined(employerProfile) &&
      (currentPath !== '/setup' || emptyOrUndefined(userProfile))
    ) {
      navigate('/setup');
    } else if (
      !emptyOrUndefined(employerProfile) &&
      userProfile?.verified === true &&
      (currentPath === '/tou' || currentPath === '/setup')
    ) {
      navigate('/');
    }
  }, [navigate, touAccepted, employerProfile, userProfile, location.pathname]);

  const getUserProfile = useCallback(
    async (userEmail: string, userJwtToken: string) => {
      try {
        if (userEmail && userJwtToken) {
          setLoadStatus(true);
          const userResult = await refetchUserProfile();
          const userProfileData = userResult.data;

          if (userProfileData && !emptyOrUndefined(userProfileData)) {
            await asyncSetUserProfile(userProfileData);
            if (userProfileData.employer) {
              const employerResult = await refetchEmployerProfile();

              if (employerResult.error) {
                const apiError = handleError(
                  employerResult.error,
                  'Failed to load employer profile',
                );
                setFailedRequest(apiError);
                setLoadStatus(false);
                return;
              }

              if (employerResult.data) {
                await asyncSetEmployerProfile(employerResult.data);
              }
            }
            setLoadStatus(false);
            return;
          }

          const employerResult = await refetchEmployerProfile();
          const employerProfileData = employerResult.data;

          if (employerProfileData && !emptyOrUndefined(employerProfileData)) {
            await asyncSetEmployerProfile(employerProfileData);
            setLoadStatus(false);
            return;
          }

          setLoadStatus(false);
          navigate('/setup');
        }
      } catch (error: unknown) {
        const apiError = handleError(error, 'Failed to load user profile');
        setFailedRequest(apiError);
        setLoadStatus(false);
      }
    },
    [
      asyncSetUserProfile,
      asyncSetEmployerProfile,
      handleError,
      navigate,
      refetchUserProfile,
      refetchEmployerProfile,
    ],
  );

  const getTouStatus = useCallback(
    async (userEmail: string, userJwtToken: string) => {
      try {
        if (userEmail && userJwtToken) {
          setLoadStatus(true);

          // Use React Query to fetch TOU status
          const result = await refetchTouStatus();

          if (result.error) {
            const apiError = handleError(
              result.error,
              'Failed to check terms of use status',
            );
            setFailedRequest(apiError);
            setLoadStatus(false);
            return;
          }

          const { data } = result;

          // A GET that started before the accept POST committed resolves
          // touStatus:false after the user already accepted; check the store
          // before bouncing them back to /tou on that stale answer.
          if (
            data &&
            data.touStatus === false &&
            !useStore.getState().touAccepted
          ) {
            setLoadStatus(false);
            navigate('/tou');
            return;
          }

          setTouAccepted(true);
          getUserProfile(userEmail, userJwtToken);
        }
      } catch (error: unknown) {
        const apiError = handleError(
          error,
          'Failed to check terms of use status',
        );
        setFailedRequest(apiError);
        setLoadStatus(false);
      }
    },
    [getUserProfile, handleError, navigate, setTouAccepted, refetchTouStatus],
  );

  useEffect(() => {
    const currentPath = location.pathname;
    currentSession()
      .then(({ jwtToken, email }) => {
        if (!touAccepted) {
          getTouStatus(email, jwtToken);
        } else if (
          (emptyOrUndefined(userProfile) ||
            emptyOrUndefined(employerProfile) ||
            !userProfile.verified) &&
          currentPath !== '/setup'
        ) {
          setUserEmail(email || '');
          getUserProfile(email, jwtToken);
        } else {
          handleNavigation();
        }
      })
      .catch(() => {
        // Session retrieval failed - handled silently
      });

    // Accessibe plugin
    const script = document.createElement('script');
    script.src = 'https://acsbapp.com/apps/app/dist/js/app.js';
    script.async = true;
    script.onload = () => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (window.acsbJS) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.acsbJS.init();
      }
    };

    const h = document.querySelector('head') || document.body;
    h.appendChild(script);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    touAccepted,
    userProfile,
    employerProfile,
    navigate,
    setUserEmail,
    handleNavigation,
  ]);

  function handleLoadingOrFailedRequest() {
    if (failedRequest) {
      return <GeneralErrorPage error={failedRequest} />;
    }
    return null;
  }

  return (
    <div>
      <LoadingOverlay open={loading} message="Loading application..." />
      {!loading &&
        (failedRequest ? (
          handleLoadingOrFailedRequest()
        ) : (
          <Routes>
            {/* The signed-out tree owns both of these paths. Someone who
                already has a session and lands on one anyway (a bookmark, a
                stale tab finishing its redirect) has nothing to do there, so
                they go to the app rather than to the catch-all's 404. */}
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route
              path="/login/callback"
              element={<Navigate to="/" replace />}
            />
            <Route
              path="*"
              element={
                <Suspense
                  fallback={<LoadingOverlay open message="Loading..." />}
                >
                  <PageNotFound />
                </Suspense>
              }
            />
            <Route path="/" element={<Layout />}>
              <Route
                path="/tou"
                element={
                  <Suspense
                    fallback={
                      <LoadingOverlay open message="Loading Terms of Use..." />
                    }
                  >
                    <TouPage />
                  </Suspense>
                }
              />
              <Route
                path="/setup"
                element={
                  <Suspense
                    fallback={
                      <LoadingOverlay open message="Loading Setup..." />
                    }
                  >
                    <SetupPage
                      setUserProfile={setUserProfile}
                      userProfile={userProfile}
                      getUserProfile={getUserProfile}
                      setFailedRequest={setFailedRequest}
                    />
                  </Suspense>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <Suspense
                    fallback={
                      <LoadingOverlay
                        open
                        message="Loading Admin Settings..."
                      />
                    }
                  >
                    <UserManagementPage />
                  </Suspense>
                }
              />

              <Route
                index
                element={
                  <Suspense
                    fallback={
                      <LoadingOverlay open message="Loading Dashboard..." />
                    }
                  >
                    <HomePage
                      setFailedRequest={setFailedRequest}
                      userProfile={userProfile}
                    />
                  </Suspense>
                }
              />
              <Route
                path="/profile"
                element={
                  <Suspense
                    fallback={
                      <LoadingOverlay open message="Loading User Profile..." />
                    }
                  >
                    <UserProfilePage />
                  </Suspense>
                }
              />
              <Route
                path="/organization"
                element={
                  <Suspense
                    fallback={
                      <LoadingOverlay open message="Loading Organization..." />
                    }
                  >
                    <OrganizationPage setFailedRequest={setFailedRequest} />
                  </Suspense>
                }
              />
              <Route
                path="/grantees"
                element={
                  <Suspense
                    fallback={
                      <LoadingOverlay open message="Loading Grantees..." />
                    }
                  >
                    <GranteesPage setFailedRequest={setFailedRequest} />
                  </Suspense>
                }
              />
              <Route
                path="/analysis"
                element={
                  <Suspense
                    fallback={
                      <LoadingOverlay open message="Loading Analysis..." />
                    }
                  >
                    <AnalysisPage setFailedRequest={setFailedRequest} />
                  </Suspense>
                }
              >
                {userProfile && !userProfile?.read_only && (
                  <Route
                    path="create"
                    element={
                      <Suspense
                        fallback={
                          <LoadingOverlay
                            open
                            message="Loading Analysis Creator..."
                          />
                        }
                      >
                        <AnalysisCreatePage />
                      </Suspense>
                    }
                  />
                )}
              </Route>
              <Route
                path="/analysis/report/:reportId"
                element={
                  <Suspense
                    fallback={
                      <LoadingOverlay
                        open
                        message="Loading Analysis Report..."
                      />
                    }
                  >
                    <AnalysisReportPage setFailedRequest={setFailedRequest} />
                  </Suspense>
                }
              />
              <Route
                path="/grantee/:granteeId"
                element={
                  <Suspense
                    fallback={
                      <LoadingOverlay
                        open
                        message="Loading Grantee Details..."
                      />
                    }
                  >
                    <GranteePage
                      userProfile={userProfile}
                      setFailedRequest={setFailedRequest}
                    />
                  </Suspense>
                }
              />
              <Route
                path="/grantee/:granteeId/add"
                element={
                  <Suspense
                    fallback={
                      <LoadingOverlay open message="Loading Grantee Form..." />
                    }
                  >
                    <GranteeFormPage setFailedRequest={setFailedRequest} />
                  </Suspense>
                }
              />
              <Route
                path="/grantee/:granteeId/edit"
                element={
                  <Suspense
                    fallback={
                      <LoadingOverlay
                        open
                        message="Loading Grantee Editor..."
                      />
                    }
                  >
                    <GranteeEditPage setFailedRequest={setFailedRequest} />
                  </Suspense>
                }
              />
              <Route
                path="/grantee/:granteeId/document/:documentId"
                element={
                  <Suspense
                    fallback={
                      <LoadingOverlay open message="Loading Document..." />
                    }
                  >
                    <DocumentPage setFailedRequest={setFailedRequest} />
                  </Suspense>
                }
              />

              <Route
                path="/risk-profile"
                element={
                  <Suspense
                    fallback={
                      <LoadingOverlay open message="Loading Risk Profiles..." />
                    }
                  >
                    <RiskProfilePage setFailedRequest={setFailedRequest} />
                  </Suspense>
                }
              />

              <Route
                path="/help"
                element={
                  <Suspense
                    fallback={
                      <LoadingOverlay open message="Loading Help Center..." />
                    }
                  >
                    <HelpCenterPage />
                  </Suspense>
                }
              />
            </Route>
            <Route path="/request-access" element={<RequestAccess />} />
          </Routes>
        ))}
    </div>
  );
}

function LoggedOutRoutes() {
  return (
    <>
      <SiteMessageBanner />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/login/callback" element={<LoginCallback />} />
        <Route path="/request-access" element={<RequestAccess />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </>
  );
}

function RoutingLogic() {
  const { isChecking, hasSession } = useSessionGate();

  if (isChecking) {
    return <LoadingOverlay open message="Loading application..." />;
  }

  return hasSession ? <RouterChildren /> : <LoggedOutRoutes />;
}

function ShowAccessibeFAB() {
  const [searchParams] = useSearchParams();

  // Check if the query parameter `showAccessibe` is true
  const showAccessibe = searchParams.get('showAccessibe') === 'true';

  return showAccessibe ? <AccessibeFAB /> : null;
}

function App() {
  return (
    <QueryProvider>
      <ThemeProvider theme={McGovernTheme}>
        <RoutingLogic />
        <DialogProvider />
        <ShowAccessibeFAB />
        <FeedbackButton />
        <FeedbackDialog />
        <NotificationManager />
      </ThemeProvider>
    </QueryProvider>
  );
}

export default App;
